import type React from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useAuth } from "@/features/auth/useAuth";
import { logout } from "@/features/auth/api";
import { cn } from "@/lib/utils";
import { Sheet } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";

interface NavItem {
  to: string;
  label: string;
}

const NAV: NavItem[] = [
  { to: "/", label: "Clients" },
  { to: "/compare", label: "Compare" },
  { to: "/overview", label: "Overview" },
  { to: "/industry", label: "Industry insights" },
  { to: "/survey-results", label: "Feedback" },
];

function isActive(item: NavItem, pathname: string): boolean {
  if (item.to === "/") return pathname === "/";
  if (item.to === "/compare") return pathname === "/compare" || pathname.startsWith("/compare/");
  return pathname.startsWith(item.to);
}

export function AppShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch {
      // Don't redirect on failure — that would look like a successful sign-out
      // while the server-side session cookie is still valid.
      window.alert("Couldn't sign you out. Please try again.");
      return;
    }
    window.location.href = "/";
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-10 bg-duress-black text-white">
        <div className="flex h-16 items-center justify-between gap-6 px-6">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2.5">
              <svg viewBox="0 0 93.5 110.5" className="w-8 h-8" aria-hidden="true" fill="#f7f7f7">
                <circle cx="29.22" cy="46.75" r="5.84"/>
                <circle cx="46.75" cy="46.75" r="5.84"/>
                <circle cx="64.28" cy="46.75" r="5.84"/>
                <path d="M79.81,13.69C71.34,5.23,59.66,0,46.75,0S22.15,5.23,13.69,13.69C5.24,22.15,0,33.84,0,46.75s5.24,24.6,13.69,33.06l29.03,29.03c2.23,2.23,5.84,2.23,8.06,0l29.03-29.03c8.45-8.46,13.69-20.15,13.69-33.06s-5.24-24.6-13.69-33.06ZM46.75,85.44c-21.37,0-38.7-17.32-38.7-38.7S25.38,8.05,46.75,8.05s38.7,17.32,38.7,38.7-17.32,38.7-38.7,38.7Z"/>
              </svg>
              <span className="font-head text-lg font-semibold tracking-tight text-white">
                Duress Vitals
              </span>
            </Link>
            <nav className="hidden gap-1 md:flex">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "border-b-2 px-3 py-[18px] -my-[18px] text-sm font-head font-medium transition-colors",
                    isActive(item, location.pathname)
                      ? "border-phoenix-orange text-white"
                      : "border-transparent text-white/60 hover:text-white hover:border-phoenix-orange/50",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-1">
            {user ? (
              <span className="hidden text-sm text-white/70 sm:inline mr-2">{user.email}</span>
            ) : null}
            <ThemeToggle />
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="hidden sm:inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">Sign out</span>
            </button>
            <button
              type="button"
              onClick={() => { setMobileOpen(true); }}
              className="md:hidden rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen} side="left">
        <div className="flex flex-col h-full pt-14 pb-6 px-4">
          <nav className="flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => { setMobileOpen(false); }}
                className={cn(
                  "rounded-md px-3 py-2.5 text-sm font-head font-medium transition-colors",
                  isActive(item, location.pathname)
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:bg-white/10 hover:text-white",
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="mt-auto border-t border-white/10 pt-4">
            {user ? (
              <p className="px-3 pb-3 text-xs text-white/40 truncate">{user.email}</p>
            ) : null}
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </Sheet>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-7xl px-6 py-8">{children}</div>
      </main>
    </div>
  );
}
